import React from 'react';
import {MenuItem} from '@mui/material';

import SelectInput from '../SelectInput';
import Loading from '../Loading';

interface ApiSelectInputProps<T> {
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: boolean;
  placeholder?: string;
  testId?: string;
  fullWidth?: boolean;
  useDataHook: () => {data: T[]; isLoading: boolean};
  getItemValue: (item: T) => string;
  getItemLabel: (item: T) => string;
}

// Generic wrapper component around SelectInput to populate options via API.
function ApiSelectInput<T>({
  name,
  value,
  onChange,
  onBlur,
  error,
  placeholder = 'Select an option',
  testId,
  fullWidth = true,
  useDataHook,
  getItemValue,
  getItemLabel,
}: ApiSelectInputProps<T>) {
  const {data = [], isLoading} = useDataHook();

  const renderItem = (item: T) => (
    <MenuItem key={getItemValue(item)} value={getItemValue(item)}>
      {getItemLabel(item)}
    </MenuItem>
  );

  const renderValue = (selected: string) => {
    const selectedItem = data.find(item => getItemValue(item) === selected);

    return selectedItem ? getItemLabel(selectedItem) : placeholder;
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <SelectInput<T>
      name={name}
      items={data}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      placeholder={placeholder}
      testId={testId}
      fullWidth={fullWidth}
      renderItem={renderItem}
      renderValue={renderValue}
    />
  );
}

export default ApiSelectInput;
